import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaAddressBook,
  FaCheckCircle,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTrash,
  FaUserEdit,
  FaUsers,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatShortDateTime,
  getInitialScopedOutlets,
  getInitials,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';

const createEmptyCustomerForm = () => ({
  name: '',
  telp: '',
  email: '',
  address: '',
});

const buildCustomerFormFromRecord = customer => ({
  name: String(customer?.name || ''),
  telp: String(customer?.telp || ''),
  email: String(customer?.email || ''),
  address: String(customer?.address || ''),
});

const normalizeCustomerRecord = customer => ({
  ...customer,
  id: customer?.id ?? '',
  outlet_id: customer?.outlet_id ?? '',
  name: String(customer?.name || '').trim(),
  telp: String(customer?.telp || '').trim(),
  email: String(customer?.email || '').trim(),
  address: String(customer?.address || '').trim(),
  created_at: customer?.created_at || '',
  updated_at: customer?.updated_at || '',
});

function PosCustomersManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(createEmptyCustomerForm);
  const [editingId, setEditingId] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => outlet.id === selectedOutlet)?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const customerSummary = useMemo(() => {
    const withEmail = customers.filter(customer => customer.email).length;
    const withAddress = customers.filter(customer => customer.address).length;

    return {
      total: totalCustomers,
      showing: customers.length,
      withEmail,
      withAddress,
    };
  }, [customers, totalCustomers]);

  const resetForm = useCallback(() => {
    setEditingId('');
    setFormData(createEmptyCustomerForm());
  }, []);

  const closeCustomerModal = useCallback(() => {
    setShowCustomerModal(false);
    resetForm();
  }, [resetForm]);

  const fetchCustomers = useCallback(async (token, outletId, page, limit, keyword) => {
    if (!outletId || !POS_API_BASE_URL) {
      setCustomers([]);
      setTotalCustomers(0);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const params = new URLSearchParams({
        outlet_id: outletId,
        page: String(page),
        limit: String(limit),
      });

      if (String(keyword || '').trim()) {
        params.set('q', String(keyword).trim());
      }

      const response = await fetch(`${POS_API_BASE_URL}api/customers/?${params.toString()}`, {
        headers: buildAuthHeaders(token),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat customer POS.'));
      }

      const { records, total } = normalizePagedRecords(data);
      setCustomers((Array.isArray(records) ? records : []).map(normalizeCustomerRecord));
      setTotalCustomers(Number(total || 0));
    } catch (error) {
      console.error('Failed to fetch POS customers:', error);
      setCustomers([]);
      setTotalCustomers(0);
      setErrorMessage(error.message || 'Gagal memuat customer POS.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;

    const initialize = async () => {
      try {
        const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
        if (!active || !token) {
          return;
        }

        setAuthToken(token);
        const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
        if (!active) {
          return;
        }

        setOutlets(scopedOutlets);
        setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
      } catch (error) {
        console.error('Failed to initialize POS customers page:', error);
        if (active) {
          setErrorMessage(error.message || 'Gagal menyiapkan halaman customer POS.');
        }
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [canAccess, currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchCustomers(authToken, selectedOutlet, currentPage, rowsPerPage, search);
  }, [authToken, currentPage, fetchCustomers, rowsPerPage, search, selectedOutlet]);

  useEffect(() => {
    closeCustomerModal();
    setCurrentPage(1);
  }, [closeCustomerModal, selectedOutlet]);

  const handleFormChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const openCreateCustomer = useCallback(() => {
    resetForm();
    setShowCustomerModal(true);
  }, [resetForm]);

  const startEdit = useCallback(customer => {
    setEditingId(String(customer.id || ''));
    setFormData(buildCustomerFormFromRecord(customer));
    setShowCustomerModal(true);
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    const isEditing = Boolean(editingId);
    setSaving(true);
    try {
      const payload = {
        outlet_id: Number(selectedOutlet),
        name: formData.name.trim(),
        telp: formData.telp.trim(),
        email: formData.email.trim(),
        address: formData.address.trim(),
      };

      const response = await fetch(
        isEditing
          ? `${POS_API_BASE_URL}api/customers/${editingId}`
          : `${POS_API_BASE_URL}api/customers/`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: buildAuthHeaders(authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(payload),
        }
      );
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menyimpan customer POS.'));
      }

      const nextPage = isEditing ? currentPage : 1;
      if (!isEditing) {
        setCurrentPage(1);
      }
      await fetchCustomers(authToken, selectedOutlet, nextPage, rowsPerPage, search);
      closeCustomerModal();
      Swal.fire('Success', isEditing ? 'Customer berhasil diperbarui.' : 'Customer berhasil ditambahkan.', 'success');
    } catch (error) {
      console.error('Failed to save POS customer:', error);
      Swal.fire('Error', error.message || 'Gagal menyimpan customer POS.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async customer => {
    const confirmed = await Swal.fire({
      title: 'Hapus customer?',
      text: `${customer.name || 'Customer ini'} akan di-soft delete dan tidak tampil lagi di daftar aktif.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Soft Delete',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#d45463',
    });

    if (!confirmed.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(`${POS_API_BASE_URL}api/customers/${customer.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal menghapus customer POS.'));
      }

      const nextPage = customers.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      }
      if (String(editingId) === String(customer.id)) {
        resetForm();
      }

      await fetchCustomers(authToken, selectedOutlet, nextPage, rowsPerPage, search);
      Swal.fire('Success', 'Customer berhasil di-soft delete.', 'success');
    } catch (error) {
      console.error('Failed to delete POS customer:', error);
      Swal.fire('Error', error.message || 'Gagal menghapus customer POS.', 'error');
    }
  }, [authToken, currentPage, customers.length, editingId, fetchCustomers, resetForm, rowsPerPage, search, selectedOutlet]);

  const columns = useMemo(
    () => [
      {
        name: 'Customer',
        sortable: true,
        grow: 1.4,
        cell: row => (
          <div className="table-identity">
            <span className="table-identity__avatar">
              {getInitials(row.name || 'CS')}
            </span>
            <div className="table-identity__meta">
              <strong>{row.name || '-'}</strong>
              <small>{row.telp || 'Nomor telepon belum diisi'}</small>
            </div>
          </div>
        ),
      },
      {
        name: 'Kontak',
        grow: 1.35,
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>{row.email || 'Tanpa email'}</strong>
            <small>{row.address || 'Alamat belum diisi'}</small>
          </div>
        ),
      },
      {
        name: 'Outlet',
        sortable: true,
        selector: () => selectedOutletName,
        cell: () => (
          <div className="pos-catalog-value-stack">
            <strong>{selectedOutletName}</strong>
            <small>Outlet aktif</small>
          </div>
        ),
      },
      {
        name: 'Update Terakhir',
        sortable: true,
        selector: row => row.updated_at || row.created_at || '',
        cell: row => (
          <div className="pos-catalog-value-stack">
            <strong>{formatShortDateTime(row.updated_at || row.created_at)}</strong>
            <small>
              <span className="status-pill">Active</span>
            </small>
          </div>
        ),
      },
      {
        name: 'Actions',
        width: '190px',
        cell: row => (
          <div className="table-action-group">
            <Button variant="outline-primary" size="sm" onClick={() => startEdit(row)}>
              <FaUserEdit /> Edit
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>
              <FaTrash /> Delete
            </Button>
          </div>
        ),
      },
    ],
    [handleDelete, selectedOutletName, startEdit]
  );

  const headerActions = (
    <>
      <Form.Select
        className="accounting-outlet-picker"
        value={selectedOutlet}
        onChange={event => setSelectedOutlet(event.target.value)}
        disabled={outlets.length === 0}
      >
        {outlets.length === 0 ? <option value="">Belum ada outlet</option> : null}
        {outlets.map(outlet => (
          <option key={outlet.id} value={outlet.id}>
            {outlet.name}
          </option>
        ))}
      </Form.Select>
      <Button
        variant="outline-primary"
        onClick={() => fetchCustomers(authToken, selectedOutlet, currentPage, rowsPerPage, search)}
        disabled={!authToken || !selectedOutlet || loading}
      >
        <FaRedoAlt /> Refresh
      </Button>
    </>
  );

  const headerMeta = (
    <>
      <div className="page-meta__chip">
        <small>Total Customer</small>
        <strong>{customerSummary.total}</strong>
      </div>
      <div className="page-meta__chip">
        <small>Outlet Aktif</small>
        <strong>{selectedOutletName}</strong>
      </div>
      <div className="page-meta__chip">
        <small>Halaman</small>
        <strong>{currentPage}</strong>
      </div>
    </>
  );

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaAddressBook}
          eyebrow="Catalog & Inventory"
          title="POS Customers"
          description="Kelola customer per outlet dari backoffice POS. Delete memakai soft delete agar riwayat data tetap aman di database."
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Customers' },
          ]}
          actions={headerActions}
          meta={headerMeta}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaUsers />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Total customer aktif</p>
                <strong>{customerSummary.total}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaCheckCircle />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Tampil di halaman ini</p>
                <strong>{customerSummary.showing}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaEnvelope />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Punya email</p>
                <strong>{customerSummary.withEmail}</strong>
              </div>
            </div>

            <div className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Outlet Aktif</span>
              <h2>{selectedOutletName}</h2>
              <p>Customer disimpan per outlet agar histori transaksi dan data kontak tetap rapi per cabang.</p>
            </div>
          </div>

          <div className="pos-catalog-stack">
            <section className="surface-card table-card pos-catalog-table-card">
              <div className="pos-catalog-toolbar">
                <div>
                  <span className="page-eyebrow">Data Customer</span>
                  <h2 className="mb-1">Daftar customer outlet</h2>
                  <p className="text-muted mb-0">
                    Search customer berdasarkan nama, telepon, email, atau alamat. Delete akan melakukan soft delete.
                  </p>
                </div>

                <div className="pos-catalog-toolbar__meta pos-catalog-toolbar__meta--split">
                  <div className="pos-catalog-toolbar__group">
                    <div className="search-control control-with-icon">
                      <span className="control-with-icon__icon">
                        <FaSearch />
                      </span>
                      <Form.Control
                        value={search}
                        onChange={event => {
                          setSearch(event.target.value);
                          setCurrentPage(1);
                        }}
                        placeholder="Cari customer"
                      />
                    </div>
                    <span className="pos-catalog-filter-chip">
                      Outlet: {selectedOutletName}
                    </span>
                    <span className="pos-catalog-filter-chip">
                      {customers.length} dari {totalCustomers} customer
                    </span>
                  </div>
                  <div className="pos-catalog-toolbar__group pos-catalog-toolbar__group--right">
                    <Button variant="primary" onClick={openCreateCustomer} disabled={!selectedOutlet}>
                      <FaPlus /> Add Customer
                    </Button>
                  </div>
                </div>
              </div>

              <AdminDataTable
                columns={columns}
                data={customers}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalCustomers}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                onChangePage={page => setCurrentPage(page)}
                onChangeRowsPerPage={(nextRowsPerPage, page) => {
                  setRowsPerPage(nextRowsPerPage);
                  setCurrentPage(page);
                }}
                persistTableHead
                emptyTitle="Belum ada customer POS"
                emptyDescription="Tambahkan customer pertama untuk outlet ini agar daftar pelanggan POS mulai terbentuk."
              />
            </section>
          </div>
        </div>

        <Modal show={showCustomerModal} onHide={closeCustomerModal} size="lg" centered>
          <Form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{editingId ? 'Perbarui customer POS' : 'Tambah customer POS'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-grid gap-3">
                <div className="pos-catalog-subsection">
                  <div className="pos-catalog-section-head">
                    <div>
                      <h3>{selectedOutletName}</h3>
                      <p className="m-0">
                        {editingId
                          ? 'Update data customer untuk outlet aktif tanpa keluar dari daftar customer.'
                          : 'Customer baru akan langsung tersimpan ke outlet aktif yang sedang dipilih.'}
                      </p>
                    </div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className="pos-catalog-badge pos-catalog-badge--info">Scope outlet aktif</span>
                    <span className="pos-catalog-badge pos-catalog-badge--success">
                      {customers.length} customer tampil
                    </span>
                  </div>
                </div>

                <div className="pos-catalog-mini-grid">
                  <Form.Group>
                    <Form.Label>Nama customer</Form.Label>
                    <Form.Control
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Masukkan nama customer"
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>No. telepon</Form.Label>
                    <Form.Control
                      name="telp"
                      value={formData.telp}
                      onChange={handleFormChange}
                      placeholder="08xxxxxxxxxx"
                      required
                    />
                  </Form.Group>
                </div>

                <div className="pos-catalog-mini-grid">
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="customer@email.com"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Outlet</Form.Label>
                    <Form.Control value={selectedOutletName} readOnly disabled />
                  </Form.Group>
                </div>

                <Form.Group>
                  <Form.Label>Alamat</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="address"
                    value={formData.address}
                    onChange={handleFormChange}
                    placeholder="Alamat customer"
                  />
                </Form.Group>

                <div className="pos-catalog-mini-grid">
                  <div className="pos-catalog-inline-item">
                    <span>
                      <FaPhone /> Nomor aktif
                    </span>
                    <strong>{formData.telp.trim() ? 'Siap disimpan' : 'Wajib diisi'}</strong>
                  </div>
                  <div className="pos-catalog-inline-item">
                    <span>
                      <FaMapMarkerAlt /> Alamat
                    </span>
                    <strong>{formData.address.trim() ? 'Tersedia' : 'Opsional'}</strong>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" type="button" onClick={closeCustomerModal}>
                Tutup
              </Button>
              <Button variant="outline-secondary" type="button" onClick={resetForm}>
                Reset
              </Button>
              <Button type="submit" disabled={saving || !selectedOutlet || !POS_API_BASE_URL}>
                {editingId ? <FaSave /> : <FaPlus />}
                {saving ? 'Menyimpan...' : editingId ? 'Update Customer' : 'Simpan Customer'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
}

export default PosCustomersManager;
